import { useTranslation } from 'react-i18next';

import { ROUTE_PATTERNS } from '@/shared/config/routes';

/**
 * Route slugs that name a report section, taken from the route patterns rather
 * than restated, so this union tracks the routes themselves.
 */
type SectionSlug =
  | typeof ROUTE_PATTERNS.IQ_SECTION_EXECUTIVE_SNAPSHOT
  | typeof ROUTE_PATTERNS.IQ_SECTION_BUSINESS_CHALLENGE
  | typeof ROUTE_PATTERNS.IQ_SECTION_CULTURE_CONTEXT
  | typeof ROUTE_PATTERNS.IQ_SECTION_BRAND_XRAY
  | typeof ROUTE_PATTERNS.IQ_SECTION_AUDIENCE_PROFILES
  | typeof ROUTE_PATTERNS.IQ_SECTION_CATEGORY_COMPETITION
  | typeof ROUTE_PATTERNS.IQ_SECTION_BUSINESS_OPPORTUNITIES
  | typeof ROUTE_PATTERNS.IQ_SECTION_MARKETING_PERFORMANCE
  | typeof ROUTE_PATTERNS.IQ_SECTION_MEDIA_LANDSCAPE
  | typeof ROUTE_PATTERNS.IQ_SECTION_CREATIVE_LANDSCAPE;

/** The i18n keys this map may hold, so they stay compile-time-checked. */
type SectionLabelKey =
  | 'analysis.analysisSidebar.executiveSnapshot'
  | 'analysis.analysisSidebar.businessChallenge'
  | 'analysis.analysisSidebar.cultureContext'
  | 'analysis.analysisSidebar.brandXRay'
  | 'analysis.analysisSidebar.audienceProfiles'
  | 'analysis.analysisSidebar.categoryCompetition'
  | 'analysis.analysisSidebar.businessOpportunities'
  | 'analysis.analysisSidebar.marketingPerformance'
  | 'analysis.analysisSidebar.mediaLandscape'
  | 'analysis.analysisSidebar.creativeLandscape';

/**
 * Keyed by `SectionSlug`, so adding a section route without a label here fails
 * `tsc` rather than silently returning undefined — which would drop the download
 * button on that section.
 */
const SECTION_LABEL_KEYS: Record<SectionSlug, SectionLabelKey> = {
  [ROUTE_PATTERNS.IQ_SECTION_EXECUTIVE_SNAPSHOT]: 'analysis.analysisSidebar.executiveSnapshot',
  [ROUTE_PATTERNS.IQ_SECTION_BUSINESS_CHALLENGE]: 'analysis.analysisSidebar.businessChallenge',
  [ROUTE_PATTERNS.IQ_SECTION_CULTURE_CONTEXT]: 'analysis.analysisSidebar.cultureContext',
  [ROUTE_PATTERNS.IQ_SECTION_BRAND_XRAY]: 'analysis.analysisSidebar.brandXRay',
  [ROUTE_PATTERNS.IQ_SECTION_AUDIENCE_PROFILES]: 'analysis.analysisSidebar.audienceProfiles',
  [ROUTE_PATTERNS.IQ_SECTION_CATEGORY_COMPETITION]: 'analysis.analysisSidebar.categoryCompetition',
  [ROUTE_PATTERNS.IQ_SECTION_BUSINESS_OPPORTUNITIES]:
    'analysis.analysisSidebar.businessOpportunities',
  [ROUTE_PATTERNS.IQ_SECTION_MARKETING_PERFORMANCE]:
    'analysis.analysisSidebar.marketingPerformance',
  [ROUTE_PATTERNS.IQ_SECTION_MEDIA_LANDSCAPE]: 'analysis.analysisSidebar.mediaLandscape',
  [ROUTE_PATTERNS.IQ_SECTION_CREATIVE_LANDSCAPE]: 'analysis.analysisSidebar.creativeLandscape',
};

const isSectionSlug = (value: string | undefined): value is SectionSlug =>
  value !== undefined && value in SECTION_LABEL_KEYS;

/**
 * Display name for a section route slug. The header sits above the sections, so
 * it has no section title of its own, but the download filename is built from
 * one — these are the same labels the rail shows.
 */
export const useSectionTitle = (section: string | undefined): string | undefined => {
  const { t } = useTranslation('analysis');
  return isSectionSlug(section) ? t(SECTION_LABEL_KEYS[section]) : undefined;
};
